/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import { Microscope } from 'lucide-react';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import { SterilityTestServices } from '../../../service/sterilityTestServices';
import { authUserID } from '../../../service/axiosConfig';
import AlertModal, { EsignModal } from '../../../widgets/alertModal';
import CustomToast from '../../../widgets/toaster';

interface SterilityTest {
  id: string;
  sterility_test_identifier: string;
  production_schedule: string | null;
  product_name: string | null;
  batch: string;
  no_of_tubes: string;
  test_method: string;
  plate_type_name: string;
  start_date: string;
  status: string;
  approval_status: string;
}


const SterilityApproval: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'Requests' | 'History'>('Requests');
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTest, setSelectedTest] = useState<SterilityTest | null>(null);
  const [activeSection, setActiveSection] = useState('Test Details');

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  // State for tracking selected row IDs
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // State for stats
  const [statsData, setStatsData] = useState({
    total: '0',
    approved: '0',
    pending: '0',
    rejected: '0'
  });
  // State for table data
  const [tableData, setTableData] = useState<SterilityTest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // State for media masters list
  const [mediaList, setMediaList] = useState<any[]>([]);
  // State for sterility test identifiers list
  const [sterilityIdentifiers, setSterilityIdentifiers] = useState<any>([]);
  // State for filters
  const [filters, setFilters] = useState({
    keyword: '',
    testMethod: '',
    batchCode: '',
    mediaType: ''
  });
  // State for full view API response
  const [viewData, setViewData] = useState<any>(null);
  // State for QA review form
  const [qaRemarks, setQaRemarks] = useState('');
  const [qaSignature, setQaSignature] = useState('');
  // State for EsignModal
  const [showEsignAlert, setShowEsignAlert] = useState(false);
  const [esignParams, setEsignParams] = useState<{ sterilityTestId: string | number; labTestStatus: number } | null>(null);
  // State for AlertModal
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertConfirmAction, setAlertConfirmAction] = useState<(() => void) | null>(null);
  // State for toast notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      const response = await SterilityTestServices.getSterilityTestLabApprovalStats();
      if (response?.status && response?.data) {
        setStatsData({
          total: response.data.total || '0',
          approved: response.data.approved || '0',
          pending: response.data.pending || '0',
          rejected: response.data.rejected || '0'
        });
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchMediaList = async () => {
      const response = await SterilityTestServices.getMediaMastersList('INH');
      if (response?.status && response?.data) {
        setMediaList(response.data);
      }
    };
    fetchMediaList();
  }, []);

  useEffect(() => {
    const fetchSterilityIdentifiers = async () => {
      const response = await SterilityTestServices.getSterilityTestIdentifiersList();
      if (response?.status && response?.data) {
        setSterilityIdentifiers(response.data);
      }
    };
    fetchSterilityIdentifiers();
  }, []);

  useEffect(() => {
    const fetchTableData = async () => {
      setIsLoading(true);
      setTableData([]); // Clear existing data before fetching
      const approvalStatus = activeTab === 'Requests' ? 'PENDING' : 'APPROVED,REJECTED';
      const response = await SterilityTestServices.getSterilityTestLabApprovalList(
        page,
        pageSize,
        'id',
        'desc',
        approvalStatus,
        filters.mediaType,
        filters.testMethod,
        filters.keyword,
        filters.batchCode
      );
      if (response?.status && response?.data) {
        setTableData(response.data);
        setTotalPages(Number(response.totalPages));
        setTotalElements(Number(response.totalElements));
      }
      setIsLoading(false);
    };
    fetchTableData();
  }, [activeTab, page, pageSize, filters]);

  
  const sections = [
    'Test Details',
    'Test Tube Details',
    'Incubation Details',
    'Observation History',
    // 'Flora Identification',
    // 'Final Result',
    'QA Review & Approval',
    'Audit Trail',
  ];

  const sectionLabels: Record<string, string> = {
    'Test Details': t('sterilityTestApproval.modal.sections.testDetails'),
    'Test Tube Details': t('sterilityTestApproval.modal.sections.testTubeDetails'),
    'Incubation Details': t('sterilityTestApproval.modal.sections.incubationDetails'),
    'Observation History': t('sterilityTestApproval.modal.sections.observationHistory'),
    // 'Flora Identification': t('sterilityTestApproval.modal.sections.floraIdentification'),
    // 'Final Result': t('sterilityTestApproval.modal.sections.finalResult'),
    'QA Review & Approval': t('sterilityTestApproval.modal.sections.qaReviewApproval'),
    'Audit Trail': t('sterilityTestApproval.modal.sections.auditTrail'),
  };

  const stats = [
    { label: t('sterilityTestApproval.stats.pendingTests'), value: statsData.pending, color: 'bg-orange-50 text-orange-600 border-orange-100', icon: 'pending_actions' },
    { label: t('sterilityTestApproval.stats.approvedTests'), value: statsData.approved, color: 'bg-green-50 text-green-600 border-green-100', icon: 'verified_user' },
    { label: t('sterilityTestApproval.stats.totalRetests'), value: statsData.rejected, color: 'bg-blue-50 text-blue-600 border-blue-100', icon: 'replay' },
  ];

  const handleViewDetails = async (test: SterilityTest) => {
    const response = await SterilityTestServices.getSterilityTestLabApprovalView(test.id);
    if (response?.status && response?.data) {
      setViewData(response.data);
      const testDetails = response.data.test_details;
      const mappedTest: SterilityTest = {
        id: test.id,
        sterility_test_identifier: testDetails.sterility_test_identifier,
        production_schedule: testDetails.production_schedule,
        product_name: testDetails.product_name,
        batch: testDetails.media_tube_batch || '',
        no_of_tubes: testDetails.no_of_test_tubes.toString(),
        test_method: testDetails.test_method,
        plate_type_name: '',
        start_date: testDetails.initiated_date_time,
        status: testDetails.activity_status,
        approval_status: response.data.qa_review_and_approval?.result || 'PENDING'
      };
      setSelectedTest(mappedTest);
      setQaRemarks('');
      setQaSignature('');
      setShowDetail(true);
    }
  };

  const handleApproveReject = (labTestStatus: number) => {
    const actionText = labTestStatus === 1 ? 'Approve' : 'Reject';
    setAlertTitle(`${actionText} Sterility Test`);
    setAlertMessage(`${actionText} this sterility test`);
    setAlertConfirmAction(() => () => {
      setEsignParams({
        sterilityTestId: viewData.sterility_test_id,
        labTestStatus: labTestStatus
      });
      setShowEsignAlert(true);
    });
    setShowAlert(true);
  };

  const handleBulkApproveReject = (labTestStatus: number) => {
    const sterilityTestIds = selectedIds.join(',');
    const actionText = labTestStatus === 1 ? 'Approve' : 'Reject';
    setAlertTitle(`${actionText} Sterility Tests`);
    setAlertMessage(`${actionText} ${selectedIds.length} sterility test(s)`);
    setAlertConfirmAction(() => () => {
      setEsignParams({
        sterilityTestId: sterilityTestIds,
        labTestStatus: labTestStatus
      });
      setShowEsignAlert(true);
    });
    setShowAlert(true);
  };

  const handleESignConfirm = async (remarks: string) => {
    if (!esignParams) return;
    const { sterilityTestId, labTestStatus } = esignParams;
    const response = await SterilityTestServices.approveSterilityTestLabApproval(
      authUserID,
      sterilityTestId,
      labTestStatus,
      remarks
    );
    if (response?.status) {
      setToastMessage(response.message);
      setToastType('success');
      setShowToast(true);
      setShowEsignAlert(false);
      setEsignParams(null);
      // If it's a single approval from modal, close modal
      if (showDetail) {
        setShowDetail(false);
      }
      // If it's bulk approval, clear selected IDs
      if (selectedIds.length > 0) {
        setSelectedIds([]);
      }
    } else {
      setToastMessage(response?.message || 'An error occurred');
      setToastType('error');
      setShowToast(true);
      setShowEsignAlert(false);
      setEsignParams(null);
    }
    // Reset page to 1 and refresh table data
    setPage(1);
    const approvalStatus = activeTab === 'Requests' ? 'PENDING' : 'APPROVED,REJECTED';
    const tableResponse = await SterilityTestServices.getSterilityTestLabApprovalList(
      1,
      pageSize,
      'id',
      'desc',
      approvalStatus,
      filters.mediaType,
      filters.testMethod,
      filters.keyword,
      filters.batchCode
    );
    // Always update table data regardless of status to handle empty responses
    setTableData(tableResponse?.data || []);
    setTotalPages(Number(tableResponse?.totalPages) || 0);
    setTotalElements(Number(tableResponse?.totalElements) || 0);
  };

  //handle table pagination 
  const handlePageChange = (newPage: any, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize) {
      setPageSize(newPageSize);
    }
  };

  // Toggle a single row
  const handleRowSelect = (id: string, checked: boolean) => {
    setSelectedIds(prev =>
      checked ? [...prev, id] : prev.filter(sid => sid !== id)
    );
  };

  return (
    <div>
      <HeaderSection />
      <PageHeader pageHeader={t('sterilityTestApproval.header.title')} pageDescription={t('sterilityTestApproval.header.subtitle')} />
      {!showDetail && (
        <div className="min-h-screen bg-[#F8FAFC] p-8 media-prep-scope">

          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
            {stats.map((stat, idx) => (
              <div key={idx} className={`p-6 rounded-2xl border ${stat.color} flex items-center justify-between shadow-sm bg-white/50 backdrop-blur-sm`}>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">{stat.label}</p>
                  <h3 className="text-3xl font-black">{stat.value}</h3>
                </div>
                <span className="material-symbols-outlined text-4xl opacity-20">{stat.icon}</span>
              </div>
            ))}
          </div>

          {/* Tabs & Main Content */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="border-b border-slate-50 px-8 flex items-center justify-between">
              <div className="flex gap-8">
                {['Requests', 'History'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab as any);
                      setPage(1);
                    }}
                    className={`py-6 text-[12px] font-black tracking-widest uppercase transition-all relative ${activeTab === tab ? 'text-[#0052CC]' : 'text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    {tab === 'Requests' ? t('sterilityTestApproval.tabs.requests') : t('sterilityTestApproval.tabs.history')}
                    {activeTab === tab && (
                      <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-1 bg-[#0052CC] rounded-t-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="p-8 bg-slate-50/50 border-b border-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-6">
                <div className="col-span-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.filters.searchLabel')}</label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                    <input
                      type="text"
                      placeholder={t('sterilityTestApproval.filters.searchPlaceholder')}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0052CC]/20 outline-none"
                      value={filters.keyword}
                      onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.filters.methodLabel')}</label>
                  <select
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                    value={filters.testMethod}
                    onChange={(e) => setFilters({ ...filters, testMethod: e.target.value })}
                  >
                    <option value={0}>{t('sterilityTestApproval.filters.allMethods')}</option>
                    <option value={1}>Direct Inoculation</option>
                    <option value={2}>Membrane Filtration</option>
                  </select>
                </div>
                {/* <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.filters.batchCodeLabel')}</label>
              <input type="text" placeholder={t('sterilityTestApproval.filters.batchCodePlaceholder')} className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none" />
            </div> */}
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.filters.batchCodeLabel2')}</label>
                  <select
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                    value={filters.batchCode}
                    onChange={(e) => setFilters({ ...filters, batchCode: e.target.value })}
                  >
                    <option value="">{t('sterilityTestApproval.filters.allBatch')}</option>
                    {sterilityIdentifiers.map((identifier: any) => (
                      <option key={identifier.id} value={identifier.id}>
                        {identifier.sterility_test_identifier}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.filters.mediaTypeLabel')}</label>
                  <select
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                    value={filters.mediaType}
                    onChange={(e) => setFilters({ ...filters, mediaType: e.target.value })}
                  >
                    <option value="">{t('sterilityTestApproval.filters.allMedia')}</option>
                    {mediaList.map((media: any) => (
                      <option key={media.id} value={media.id}>
                        {media.plate_type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-3">
                  {/* <button
                    className="flex-1 bg-[#0052CC] text-white py-2.5 rounded-xl text-xs font-bold hover:bg-[#0041a3] transition-colors"
                    onClick={() => setPage(1)}
                  >
                    {t('sterilityTestApproval.filters.searchButton')}
                  </button> */}
                  <button
                    className="px-3 bg-white border border-slate-200 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                    onClick={() => {
                      setFilters({ keyword: '', testMethod: '', batchCode: '', mediaType: '' });
                      setPage(1);
                    }}
                  >
                    <span className="material-symbols-outlined text-sm">restart_alt</span>
                  </button>
                </div>
              </div>
            </div>

            {/* approval or reject buttons */}
            {selectedIds.length > 0 && activeTab === 'Requests' && (
            <div className="flex flex-wrap gap-4 pb-4 ms-8">
              <button
                className="px-10 py-2 bg-green-600 text-white rounded-2xl text-sm font-black shadow-lg shadow-green-600/20 hover:bg-green-700 transition-all flex items-center gap-1"
                onClick={() => handleBulkApproveReject(1)}
              >
                <span className="material-symbols-outlined text-lg">check_circle</span>
                {t('sterilityTestApproval.qaReview.approveButton')}
              </button>
              <button
                className="px-10 py-2 bg-red-500 text-white rounded-2xl text-sm font-black shadow-lg shadow-red-500/20 hover:bg-red-600 transition-all flex items-center gap-1"
                onClick={() => handleBulkApproveReject(0)}
              >
                <span className="material-symbols-outlined text-lg">cancel</span>
                {t('sterilityTestApproval.qaReview.rejectButton')}
              </button>
              {/* <button className="px-10 py-2 bg-purple-600 text-white rounded-2xl text-sm font-black shadow-lg shadow-purple-600/20 hover:bg-purple-700 transition-all flex items-center gap-1">
                <span className="material-symbols-outlined text-lg">refresh</span>
                {t('sterilityTestApproval.qaReview.requestRetestButton')}
              </button> */}
            </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="tableHeadRow text-center">
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.slNo')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.sterilityTestId')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.productionSchedule')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.productName')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.batch')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.tubes')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.method')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.media')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.startDate')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.result')}</th>
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.status')}</th>
                    {activeTab === 'Requests' && (
                      <th className="tableHeadData2 px-4 py-3">
                        <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                      </th>
                    )}
                    <th className="tableHeadData2">{t('sterilityTestApproval.table.action')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {isLoading ? (
                    <tr>
                      <td colSpan={activeTab === 'Requests' ? 13 : 12} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <div className="w-8 h-8 border-3 border-[#0052CC] border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-xs text-slate-500">Loading data...</p>
                        </div>
                      </td>
                    </tr>
                  ) : tableData.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'Requests' ? 13 : 12} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <span className="material-symbols-outlined text-4xl text-slate-300">inbox</span>
                          <p className="text-sm text-slate-500">No data available</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tableData.map((test, idx) => (
                      <tr key={test.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-xs font-bold text-slate-500">{(idx + 1).toString().padStart(2, '0')}</td>
                        <td className="px-6 py-4 text-xs font-black text-[#0052CC]">{test.sterility_test_identifier}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-600">{test.production_schedule || '-'}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">{test.product_name || '-'}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-600">{test.batch}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-600">{test.no_of_tubes} {t('sterilityTestApproval.table.tubesUnit')}</td>
                        <td className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase">{test.test_method}</td>
                        <td className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase">{test.plate_type_name}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-600">{test.start_date}</td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest ${test.status === 'COMPLETED' ? 'bg-green-50 text-green-600' :
                              test.status === 'FAILED' ? 'bg-red-50 text-red-600' :
                                test.status === 'RETEST' ? 'bg-purple-50 text-purple-600' : 'bg-slate-100 text-slate-400'
                            }`}>
                            {test.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${test.approval_status === 'PENDING' ? 'bg-orange-500 animate-pulse' :
                                test.approval_status === 'APPROVED' ? 'bg-green-500' : 'bg-red-500'
                              }`} />
                            <span className="text-xs font-bold text-slate-700">{test.approval_status}</span>
                          </div>
                        </td>
                        {activeTab === 'Requests' && (
                          <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedIds.includes(test.id)}
                              onChange={(e) => handleRowSelect(test.id, e.target.checked)}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleViewDetails(test)}
                            className="text-[10px] font-black text-[#0052CC] uppercase tracking-widest hover:underline"
                          >
                            {t('sterilityTestApproval.table.viewDetails')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />

        </div>
      )}

      {showDetail && selectedTest && (
        <div className="min-h-screen bg-[#F8FAFC] p-8 media-prep-scope">
            {/* Modal Header */}
            <div className="bg-[#0F2942] p-6 text-white shrink-0">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-6">
                  <button
                    onClick={() => setShowDetail(false)}
                    className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  >
                    <span className="material-symbols-outlined">arrow_back</span>
                  </button>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-black tracking-tight">{selectedTest.sterility_test_identifier}</h2>
                      <span className={`px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${selectedTest.approval_status === 'PENDING' ? 'border-orange-400/30 text-orange-400 bg-orange-400/10' : 'border-green-400/30 text-green-400 bg-green-400/10'
                        }`}>
                        {selectedTest.approval_status}
                      </span>
                    </div>
                    <p className="text-white/50 text-[11px] font-bold uppercase tracking-wider mt-1">
                      {selectedTest.product_name || '-'} • {selectedTest.batch} • {selectedTest.test_method}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">{t('sterilityTestApproval.modal.expectedCompletion')}</p>
                    <p className="text-sm font-black text-white">{selectedTest.start_date}</p>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className={`px-4 py-2 rounded-xl border-2 font-black text-xs uppercase tracking-widest ${selectedTest.status === 'COMPLETED' ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-slate-500/30 text-slate-400'
                    }`}>
                    {t('sterilityTestApproval.modal.resultPrefix')} {selectedTest.status}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Left Navigation */}
              <div className="w-72 bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-1 shrink-0 overflow-y-auto">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 mb-4">{t('sterilityTestApproval.modal.navigation')}</p>
                {sections.map((section) => (
                  <button
                    key={section}
                    onClick={() => setActiveSection(section)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-[12px] font-bold transition-all flex items-center justify-between group ${activeSection === section
                      ? 'bg-white text-[#0052CC] shadow-sm border border-slate-200'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                      }`}
                  >
                    {sectionLabels[section]}
                    {activeSection === section && (
                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto p-10 bg-white custom-scrollbar">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="max-w-4xl"
                  >
                    {/* Section Heading */}
                    <div className="mb-10">
                      <h3 className="text-2xl font-black text-slate-800 tracking-tight">{sectionLabels[activeSection]}</h3>
                      <p className="text-slate-500 text-sm mt-1">{t('sterilityTestApproval.modal.sectionSubtitle')}</p>
                    </div>

                    {/* Section Content */}
                    {activeSection === 'Test Details' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-10">
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.sterilityTestId')}</p>
                          <p className="text-sm font-black text-[#0052CC]">{selectedTest.sterility_test_identifier}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.productName')}</p>
                          <p className="text-sm font-bold text-slate-800">{selectedTest.product_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.batchCode')}</p>
                          <p className="text-sm font-black text-slate-700">{selectedTest.batch}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.productionSchedule')}</p>
                          <p className="text-sm font-bold text-slate-600">{selectedTest.production_schedule || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.testMethod')}</p>
                          <p className="text-sm font-bold text-slate-800">{selectedTest.test_method}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.testType')}</p>
                          <p className="text-sm font-bold text-slate-800">Routine Analysis</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.initiatedBy')}</p>
                          <p className="text-sm font-bold text-slate-800">-</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.initiatedDateTime')}</p>
                          <p className="text-sm font-bold text-slate-800">{selectedTest.start_date}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.activityStatus')}</p>
                          <p className="text-sm font-black text-green-600">{selectedTest.status}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.testDetails.noOfTestTubes')}</p>
                          <p className="text-sm font-black text-slate-800">{selectedTest.no_of_tubes} {t('sterilityTestApproval.table.tubesUnit')}</p>
                        </div>
                      </div>
                    )}

                    {activeSection === 'Test Tube Details' && (
                      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.testTubeDetails.testTubeId')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.testTubeDetails.serialNumber')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.testTubeDetails.media')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.testTubeDetails.mediaLot')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.testTubeDetails.tubeStatus')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {viewData?.test_tube_details?.map((tube: any) => (
                              <tr key={tube.tube_id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-6 py-4 text-xs font-black text-[#0052CC]">{tube.tube_id}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-800">{tube.serial_number}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-600">{tube.media}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-500">{tube.lot_number}</td>
                                <td className="px-6 py-4">
                                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-green-50 text-green-600 uppercase">
                                    {tube.tube_status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {activeSection === 'Incubation Details' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-10">
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.incubatorId')}</p>
                          <p className="text-sm font-black text-slate-800">{viewData?.incubation_details?.incubator_id || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.startDateTime')}</p>
                          <p className="text-sm font-bold text-slate-800">{selectedTest.start_date}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.summary')}</p>
                          <p className="text-sm font-bold text-slate-800">{viewData?.incubation_details?.summary || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.duration')}</p>
                          <p className="text-sm font-bold text-slate-800">{viewData?.incubation_details?.duration || 0} Days</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.temperature')}</p>
                          <p className="text-sm font-black text-purple-600">{viewData?.incubation_details?.temperature || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.incubationStatus')}</p>
                          <span className="text-[10px] font-black px-3 py-1 rounded-full bg-green-50 text-green-600 uppercase tracking-widest border border-green-100">{viewData?.incubation_details?.status || '-'}</span>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.incubationDetails.startedBy')}</p>
                          <p className="text-sm font-bold text-slate-800">{viewData?.test_details?.initiated_by || '-'}</p>
                        </div>
                      </div>
                    )}

                    {activeSection === 'Observation History' && (
                      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.day')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.dateTime')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.tubeId')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.observation')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.remarks')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.observationHistory.by')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {viewData?.observation_history?.map((obs: any, i: number) => (
                              <tr key={i}>
                                <td className="px-6 py-4 text-xs font-black text-slate-800">Day {obs.day}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-500">{obs.date_time || '-'}</td>
                                <td className="px-6 py-4 text-xs font-black text-[#0052CC]">{obs.tube_id}</td>
                                <td className="px-6 py-4">
                                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-green-50 text-green-600 uppercase">
                                    {obs.observation || '-'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-xs text-slate-600">{obs.remarks || '-'}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-700">{obs.by || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* {activeSection === 'Flora Identification' && (
                      <div className="p-10 rounded-[32px] border border-slate-100 bg-slate-50/50 flex flex-col items-center justify-center text-center">
                        <Microscope className="w-12 h-12 text-slate-200 mb-4" strokeWidth={1.5} />
                        <h4 className="text-lg font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.floraIdentification.title')}</h4>
                        <p className="text-slate-400 text-sm mt-2 max-w-xs">{t('sterilityTestApproval.floraIdentification.message')}</p>
                      </div>
                    )} */}

                    {/* {activeSection === 'Final Result' && (
                      <div className="space-y-10">
                        <div className={`p-10 rounded-[32px] border-4 flex flex-col items-center justify-center text-center ${selectedTest.status === 'COMPLETED' ? 'border-green-100 bg-green-50/30' : 'border-red-100 bg-red-50/30'
                          }`}>
                          <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${selectedTest.status === 'COMPLETED' ? 'bg-green-600 text-white' : 'bg-red-500 text-white'
                            }`}>
                            <span className="material-symbols-outlined text-4xl">{selectedTest.status === 'COMPLETED' ? 'check' : 'close'}</span>
                          </div>
                          <h4 className={`text-4xl font-black tracking-tighter ${selectedTest.status === 'COMPLETED' ? 'text-green-600' : 'text-red-600'
                            }`}>{selectedTest.status}</h4>
                          <p className="text-slate-500 font-bold mt-4 max-w-md">
                            {selectedTest.status === 'COMPLETED'
                              ? t('sterilityTestApproval.finalResult.passMessage')
                              : t('sterilityTestApproval.finalResult.failMessage')}
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-10">
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.finalResult.resultDateTime')}</p>
                            <p className="text-sm font-bold text-slate-800">{selectedTest.start_date}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sterilityTestApproval.finalResult.determinedBy')}</p>
                            <p className="text-sm font-bold text-slate-800">-</p>
                          </div>
                        </div>
                      </div>
                    )} */}

                    {activeSection === 'QA Review & Approval' && (
                      <div className="space-y-10">
                        {viewData?.qa_review_and_approval?.result && viewData?.qa_review_and_approval?.result !== 'PENDING' ? (
                          <div className={`${viewData?.qa_review_and_approval?.result === 'APPROVED' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'} border p-8 rounded-3xl`}>
                            <h4 className={`${viewData?.qa_review_and_approval?.result === 'APPROVED' ? 'text-green-700' : 'text-red-700'} font-black uppercase text-[10px] tracking-widest mb-6`}>{t('sterilityTestApproval.qaReview.approvalInformation')}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-10">
                              <div>
                                <p className="text-[10px] font-bold uppercase mb-1">{t('sterilityTestApproval.qaReview.status')}</p>
                                <p className={`text-sm font-black ${viewData?.qa_review_and_approval?.result === 'APPROVED' ? 'text-green-700' : 'text-red-700'}`}>{viewData?.qa_review_and_approval?.result || '-'}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-bold uppercase mb-1">{t('sterilityTestApproval.qaReview.reviewedBy')}</p>
                                <p className={`text-sm font-bold ${viewData?.qa_review_and_approval?.result === 'APPROVED' ? 'text-green-700' : 'text-red-700'}`}>{viewData?.qa_review_and_approval?.determined_by || '-'}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-bold uppercase mb-1">{t('sterilityTestApproval.qaReview.reviewDate')}</p>
                                <p className={`text-sm font-black ${viewData?.qa_review_and_approval?.result === 'APPROVED' ? 'text-green-700' : 'text-red-700'}`}>{viewData?.qa_review_and_approval?.result_date_time || '-'}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                              {/* <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.qaReview.qaRemarksLabel')}</label>
                                <textarea
                                  placeholder={t('sterilityTestApproval.qaReview.qaRemarksPlaceholder')}
                                  className="w-full h-32 p-5 bg-slate-50 border border-slate-200 rounded-2xl text-xs focus:ring-2 focus:ring-[#0052CC]/10 outline-none resize-none"
                                  value={qaRemarks}
                                  onChange={(e) => setQaRemarks(e.target.value)}
                                />
                              </div> */}
                              <div className="flex flex-col gap-6">
                                {/* <div>
                                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">{t('sterilityTestApproval.qaReview.signatureLabel')}</label>
                                  <input
                                    type="password"
                                    placeholder="••••••"
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs focus:ring-2 focus:ring-[#0052CC]/10 outline-none"
                                    value={qaSignature}
                                    onChange={(e) => setQaSignature(e.target.value)}
                                  />
                                </div> */}
                                <div className="p-5 bg-[#0052CC]/5 rounded-2xl border border-[#0052CC]/10">
                                  <p className="text-[10px] text-[#0052CC] font-bold leading-relaxed">
                                    {t('sterilityTestApproval.qaReview.certifyMessage')}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-4">
                              <button
                                className="px-10 py-4 bg-green-600 text-white rounded-2xl text-sm font-black shadow-lg shadow-green-600/20 hover:bg-green-700 transition-all flex items-center gap-3"
                                onClick={() => handleApproveReject(1)}
                              >
                                <span className="material-symbols-outlined">check_circle</span>
                                {t('sterilityTestApproval.qaReview.approveButton')}
                              </button>
                              <button
                                className="px-10 py-4 bg-red-500 text-white rounded-2xl text-sm font-black shadow-lg shadow-red-500/20 hover:bg-red-600 transition-all flex items-center gap-3"
                                onClick={() => handleApproveReject(0)}
                              >
                                <span className="material-symbols-outlined">cancel</span>
                                {t('sterilityTestApproval.qaReview.rejectButton')}
                              </button>
                              {/* <button className="px-10 py-4 bg-purple-600 text-white rounded-2xl text-sm font-black shadow-lg shadow-purple-600/20 hover:bg-purple-700 transition-all flex items-center gap-3">
                                <span className="material-symbols-outlined">refresh</span>
                                {t('sterilityTestApproval.qaReview.requestRetestButton')}
                              </button> */}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'Audit Trail' && (
                      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.auditTrail.dateTime')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.auditTrail.user')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.auditTrail.action')}</th>
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityTestApproval.auditTrail.details')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {viewData?.audit_trail?.map((item: any, i: number) => (
                              <tr key={i}>
                                <td className="px-6 py-4 text-xs font-bold text-slate-500">{item.date_time || '-'}</td>
                                <td className="px-6 py-4 text-xs font-bold text-slate-800">{item.by || '-'}</td>
                                <td className="px-6 py-4 text-xs font-black text-[#0052CC] uppercase tracking-wider">{item.action || '-'}</td>
                                <td className="px-6 py-4 text-xs text-slate-600">{item.details || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
        </div>
      )}

      <AlertModal
        show={showAlert}
        onHide={() => setShowAlert(false)}
        title={alertTitle}
        message={alertMessage}
        onConfirm={() => {
          setShowAlert(false);
          if (alertConfirmAction) alertConfirmAction();
        }}
      />

      <EsignModal
        show={showEsignAlert}
        onHide={() => setShowEsignAlert(false)}
        onConfirm={handleESignConfirm}
        needRemraks={true}
        screenName="Sterility Test Approval"
      />

      <CustomToast
        show={showToast}
        message={toastMessage}
        toastType={toastType}
        delay={3000}
        onClose={() => setShowToast(false)}
      />

    </div>
  );
};

export default SterilityApproval;